import { useState } from 'react';

import {
  LlmOutputPreferencesForm,
  SettingsListGroup,
  SettingsListRow,
  SettingsPageFrame,
} from '@eluve/blocks';
import type { DetailLevel, Format } from '@eluve/blocks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Separator,
  Text,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { getPreferenceLabel } from '@eluve/llm-outputs';

import {
  DEFAULT_DETAIL_LEVEL,
  DEFAULT_FORMAT,
  type PreferenceFormState,
  useChatSettings,
} from './useChatSettings';

export const ChatOutputPreferencesPage = () => {
  const { preferences, savePreference, deletePreferenceWithUnlink } =
    useChatSettings();
  const toast = useToast();
  const { t } = useTranslation();

  const [editingPreference, setEditingPreference] =
    useState<PreferenceFormState | null>(null);
  const [showDeletePrefDialog, setShowDeletePrefDialog] = useState<
    string | null
  >(null);

  const handleCreatePreference = () => {
    setEditingPreference({
      id: '',
      name: '',
      format: DEFAULT_FORMAT,
      detailLevel: DEFAULT_DETAIL_LEVEL,
      customInstructions: '',
      isNew: true,
    });
  };

  const handleEditPreference = (prefId: string) => {
    const pref = preferences.find((p) => p.id === prefId);
    if (!pref) return;
    setEditingPreference({
      id: pref.id,
      name: pref.name ?? '',
      format: (pref.format as Format) ?? DEFAULT_FORMAT,
      detailLevel: (pref.detailLevel as DetailLevel) ?? DEFAULT_DETAIL_LEVEL,
      customInstructions: pref.customInstructions ?? '',
    });
  };

  const handleSavePreference = async () => {
    if (!editingPreference) return;
    try {
      await savePreference(editingPreference);
      setEditingPreference(null);
    } catch {
      toast.error(t('settings.chat.savePreferenceFailed'));
    }
  };

  const handleDeletePreference = async (prefId: string) => {
    try {
      await deletePreferenceWithUnlink(prefId);
      setShowDeletePrefDialog(null);
    } catch {
      toast.error(t('settings.chat.deletePreferenceFailed'));
    }
  };

  return (
    <SettingsPageFrame
      title={t('settings.chat.outputPreferences')}
      parentTitle={t('settings.chat.title')}
      backTo=".."
      description={t('settings.chat.outputPreferencesPageDescription')}
    >
      <SettingsListGroup
        countLabel={t(
          preferences.length === 1
            ? 'settings.chat.preferenceCountOne'
            : 'settings.chat.preferenceCountMany',
          { count: preferences.length },
        )}
        actions={
          <Button variant="outline" size="sm" onClick={handleCreatePreference}>
            <Icon name="Plus" size="auto" />
            {t('settings.common.add')}
          </Button>
        }
      >
        {preferences.length > 0 ? (
          preferences.map((pref, i) => (
            <div key={pref.id}>
              <SettingsListRow
                editLabel={t('settings.chat.editPreferenceLabel', {
                  label: getPreferenceLabel(pref),
                })}
                deleteLabel={t('settings.chat.deletePreferenceLabel', {
                  label: getPreferenceLabel(pref),
                })}
                onEdit={() => handleEditPreference(pref.id)}
                onDelete={() => setShowDeletePrefDialog(pref.id)}
              >
                <Text inline size="sm" weight="semibold">
                  {getPreferenceLabel(pref)}
                </Text>
              </SettingsListRow>
              {i < preferences.length - 1 && <Separator className="my-0" />}
            </div>
          ))
        ) : (
          <Text size="sm" color="tertiary" className="px-6 py-7">
            {t('settings.chat.noOutputPreferences')}
          </Text>
        )}
      </SettingsListGroup>

      <Dialog
        open={editingPreference !== null}
        onOpenChange={(open) => {
          if (!open) setEditingPreference(null);
        }}
      >
        <DialogContent size="xl" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>
              {editingPreference?.isNew
                ? t('settings.chat.newOutputPreference')
                : t('settings.chat.editOutputPreference')}
            </DialogTitle>
          </DialogHeader>
          {editingPreference && (
            <LlmOutputPreferencesForm
              name={editingPreference.name}
              onNameChange={(name) =>
                setEditingPreference((prev) =>
                  prev ? { ...prev, name } : null,
                )
              }
              format={editingPreference.format}
              detailLevel={editingPreference.detailLevel}
              customInstructions={editingPreference.customInstructions}
              onFormatChange={(format) =>
                setEditingPreference((prev) =>
                  prev ? { ...prev, format } : null,
                )
              }
              onDetailLevelChange={(detailLevel) =>
                setEditingPreference((prev) =>
                  prev ? { ...prev, detailLevel } : null,
                )
              }
              onCustomInstructionsChange={(customInstructions) =>
                setEditingPreference((prev) =>
                  prev ? { ...prev, customInstructions } : null,
                )
              }
            />
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingPreference(null)}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleSavePreference}>
              {t('settings.common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={showDeletePrefDialog !== null}
        onOpenChange={(open) => {
          if (!open) setShowDeletePrefDialog(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('settings.chat.deletePreferenceTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.chat.deletePreferenceDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (showDeletePrefDialog) {
                  handleDeletePreference(showDeletePrefDialog);
                }
              }}
            >
              {t('settings.common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsPageFrame>
  );
};
